import React, { useEffect, useState, useMemo } from 'react';
import axios from '../api/axios';
import './ApplicationView.css';

export default function ApplicationView({ applicationId, onClose, onChanged, mode = 'view' }) {
  /* ===== locale ===== */
  const [locale, setLocale] = useState(() => localStorage.getItem('locale') || 'kz');
  useEffect(() => {
    const readLocale = () => {
      const l = localStorage.getItem('locale') || 'kz';
      setLocale(prev => (prev !== l ? l : prev));
    };
    const onStorage = (e) => { if (e.key === 'locale') readLocale(); };
    const onCustom = () => readLocale();
    const onVisibility = () => { if (!document.hidden) readLocale(); };
    window.addEventListener('storage', onStorage);
    window.addEventListener('locale:changed', onCustom);
    document.addEventListener('visibilitychange', onVisibility);
    readLocale();
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('locale:changed', onCustom);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  /* ===== i18n ===== */
  const t = useMemo(() => ({
    kz: {
      loading: 'Жүктелуде...',
      notFound: 'Өтініш табылмады',
      prevBtn: 'Алдыңғы сараптама',
      open: 'ашу',
      close: 'жабу',
      prevTitle: 'Алдыңғы сараптама туралы',
      mainTitle: 'Негізгі ақпарат',
      publisher: 'Баспа',
      authors: 'Авторлар',
      grade: 'Сынып',
      subject: 'Пән',
      devLang: 'Әзірлеу тілі',
      teachLang: 'Оқыту тілі',
      category: 'Санат',
      serviceType: 'Қызмет түрі',
      status: 'Күйі',
      avgScore: 'Орташа ұпай',
      decisionDate: 'Шешім күні',
      decisionResult: 'Сараптама нәтижесі',
      name: 'Атауы',
      archive: '📦 Архив:',
      downloadArchive: 'Архивті жүктеу',
      downloadFinal: 'Қорытынды құжатты жүктеу',
      filesTitle: 'Қосылған файлдар',
      fileLink: 'Электронды оқулық (сілтеме)',
      section: 'бөлім',
      logs: 'Логтар',
      govDecision: 'Мемлекеттік қызмет шешімі',
      approved: '✅ Қабылданды',
      returned: '❌ Өңдеуге қайтарылды',
      comment: 'Пікір',
      decidedBy: 'Қабылдаған',
      date: 'Күні',
      signingStatus: 'Қол қою статусы',
      approvedYes: '✅ Расталды',
      pending: '⏳ Күтілуде',
      accept: 'Қабылдау',
      return: 'Өңдеуге қайтару',
      returnTitle: 'Өңдеуге қайтару',
      returnLabel: 'Қайтару себебі (міндетті)',
      returnPlaceholder: 'Мысалы: файл толық емес, қосымша анықтама қажет...',
      returnConfirm: 'Қайтаруды растау',
      cancel: 'Болдырмау',
      toastSuccess: '✅ Шешім сәтті қабылданды!',
      toastDuplicate: '❌ Бұл өтінішке шешім бұрын қабылданған.',
      errGeneric: '❌ Қате орын алды!',
      prevLoadErr: 'Алдыңғы сараптама туралы мәліметті жүктеу мүмкін болмады.',
      fileDownloadErr: '❌ Файлды жүктеу мүмкін болмады',
      required: 'Түсіндірме міндетті.',
      sectionNum: '-бөлім',
    },
    ru: {
      loading: 'Загрузка...',
      notFound: 'Заявка не найдена',
      prevBtn: 'Предыдущая экспертиза',
      open: 'открыть',
      close: 'закрыть',
      prevTitle: 'О предыдущей экспертизе',
      mainTitle: 'Основная информация',
      publisher: 'Издательство',
      authors: 'Авторы',
      grade: 'Класс',
      subject: 'Предмет',
      devLang: 'Язык разработки',
      teachLang: 'Язык обучения',
      category: 'Категория',
      serviceType: 'Тип услуги',
      status: 'Статус',
      avgScore: 'Средний балл',
      decisionDate: 'Дата решения',
      decisionResult: 'Результат экспертизы',
      name: 'Название',
      archive: '📦 Архив:',
      downloadArchive: 'Скачать архив',
      downloadFinal: 'Скачать итоговый документ',
      filesTitle: 'Прикрепленные файлы',
      fileLink: 'Электронный учебник (ссылка)',
      section: 'раздел',
      logs: 'Логи',
      govDecision: 'Решение госуслуги',
      approved: '✅ Принято',
      returned: '❌ Возвращено на доработку',
      comment: 'Комментарий',
      decidedBy: 'Принял',
      date: 'Дата',
      signingStatus: 'Статус подписания',
      approvedYes: '✅ Подтверждено',
      pending: '⏳ Ожидается',
      accept: 'Принять',
      return: 'Вернуть на доработку',
      returnTitle: 'Возврат на доработку',
      returnLabel: 'Причина возврата (обязательно)',
      returnPlaceholder: 'Например: неполный файл, требуется доп. справка...',
      returnConfirm: 'Подтвердить возврат',
      cancel: 'Отменить',
      toastSuccess: '✅ Решение успешно принято!',
      toastDuplicate: '❌ По этой заявке уже есть решение.',
      errGeneric: '❌ Произошла ошибка!',
      prevLoadErr: 'Не удалось загрузить данные о предыдущей экспертизе.',
      fileDownloadErr: '❌ Не удалось скачать файл',
      required: 'Комментарий обязателен.',
      sectionNum: '-й раздел',
    },
  }[locale]), [locale]);

  /* ===== state ===== */
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState('');
  const [department, setDepartment] = useState('');
  const [userName, setUserName] = useState('');
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [logsVisible, setLogsVisible] = useState(false);
  const [dlProgress, setDlProgress] = useState({});

  // Return modal
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnComment, setReturnComment] = useState('');
  const [returnError, setReturnError] = useState('');

  // Previous expertise section
  const [prevOpen, setPrevOpen] = useState(false);
  const [prevLoading, setPrevLoading] = useState(false);
  const [prevError, setPrevError] = useState('');
  const [previousData, setPreviousData] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL || 'https://expertise.gov.kz';

  const joinUrl = (base, path) =>
  `${String(base).replace(/\/$/, "")}/${String(path).replace(/^\//, "")}`;


  /* ===== helpers ===== */
  const pickName = (obj) => {
    if (!obj) return '-';
    if (typeof obj === 'string') return obj;
    if (locale === 'ru') return obj.name_ru || obj.name || obj.name_kz || '-';
    return obj.name_kz || obj.name || obj.name_ru || '-';
  };
  const fmtDate = (d) => d ? new Date(d).toLocaleString(locale === 'ru' ? 'ru-RU' : 'kk-KZ') : '-';

 const isGosService = useMemo(() => {
  const dep = (department || '').toLowerCase().trim();
  const variants = [
    'сараптаманы жоспарлау және мемлекеттік қызметтер бөлімі',
    'отдел планирования экспертизы и государственных услуг'
  ];
  return variants.includes(dep);
}, [department]);

  const statusName = useMemo(() => {
    const k = application?.internalStatus?.name_kz || application?.internal_status?.name_kz || '';
    const r = application?.internalStatus?.name_ru || application?.internal_status?.name_ru || '';
    return locale === 'ru' ? (r || k) : (k || r);
  }, [application, locale]);

  const isNewOrReturnedApplication = useMemo(() => {
  const s = (statusName || '').toLowerCase().trim();
  const variants = new Set([
    'жаңа өтініш',
    'өңдеуден келген',
    'новая заявка',
    'возвращено с обработки'
  ]);
  return variants.has(s);
}, [statusName]);

  const noDecisionYet = useMemo(() => {
  const s = (statusName || '').toLowerCase().trim();
  return !application?.decision || ['өңдеуден келген','возвращено с обработки'].includes(s);
}, [application, statusName]);

  const canMakeDecision = useMemo(() => {
    return isGosService && isNewOrReturnedApplication && noDecisionYet;
  }, [isGosService, isNewOrReturnedApplication, noDecisionYet]);

  const showDecisionUI = mode === 'decision' && isGosService && isNewOrReturnedApplication && noDecisionYet;

  const hasPrevious = useMemo(() => {
    const a = application;
    if (!a) return false;
    return Boolean(
      a.parent_application_id ||
      a.parentApplicationId ||
      a.parent ||
      a.previous ||
      a.previous_expertise ||
      a.previousExpertise
    );
  }, [application]);

  /* ===== data fetching ===== */
  useEffect(() => {
    if (applicationId) {
      loadApplication();
      loadProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, locale]); // ⬅ при смене языка перезапросим (для серверных локализованных полей)

  const authHeaders = useMemo(() => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Accept-Language': locale,
  }), [locale]);

  const loadApplication = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/ebmso/applications/${applicationId}`, { headers: authHeaders });
      setApplication(res.data.application);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const loadProfile = async () => {
    try {
      const res = await axios.get('/internal/profile', { headers: authHeaders });
      setUserRole((res.data.role?.name || '').toLowerCase());
      setDepartment(res.data.department?.name || '');
      setUserName(res.data.name || 'user');
    } catch (error) {
    }
  };

  const togglePrevious = async () => {
    const next = !prevOpen;
    setPrevOpen(next);
    if (!next) return;
    if (previousData) return;

    const fromPayload =
      application?.previous_expertise ||
      application?.previousExpertise ||
      application?.previous ||
      application?.parent;

    if (fromPayload) {
      setPreviousData(fromPayload);
      return;
    }

    try {
      setPrevLoading(true);
      setPrevError('');
      const res = await axios.get(
        `/ebmso/applications/${applicationId}/previous-expertise`,
        { headers: authHeaders }
      );
      setPreviousData(res.data.previous || res.data.application || res.data);
    } catch (e1) {
      try {
        const parentId =
          application?.parent_application_id ||
          application?.parentApplicationId ||
          application?.parent?.id ||
          null;
        if (!parentId) throw new Error('no-parent');
        const res2 = await axios.get(`/ebmso/applications/${parentId}`, { headers: authHeaders });
        setPreviousData(res2.data.application);
      } catch (e2) {
        setPrevError(t.prevLoadErr);
      }
    } finally {
      setPrevLoading(false);
    }
  };

  const handleDecision = async (decisionType) => {
    if (!applicationId) return;

    if (decisionType === 'return') {
      setShowReturnModal(true);
      setReturnError('');
      return;
    }

    setDecisionLoading(true);
    try {
      await axios.post(
        `/ebmso/applications/${applicationId}/decision`,
        { decision: decisionType },
        { headers: authHeaders }
      );
      alert(t.toastSuccess);
      await loadApplication();
      if (typeof onChanged === 'function') onChanged();
    } catch (error) {
      if (error.response?.status === 409) {
        alert(t.toastDuplicate);
      } else {
        alert(error.response?.data?.message || t.errGeneric);
      }
    } finally {
      setDecisionLoading(false);
    }
  };

  const submitReturnDecision = async () => {
    const comment = returnComment.trim();
    if (!comment) {
      setReturnError(t.required);
      return;
    }
    setDecisionLoading(true);
    setReturnError('');
    try {
      await axios.post(
        `/ebmso/applications/${applicationId}/decision`,
        { decision: 'return', comment },
        { headers: authHeaders }
      );
      alert(t.toastSuccess);
      setShowReturnModal(false);
      setReturnComment('');
      await loadApplication();
      if (typeof onChanged === 'function') onChanged();
    } catch (error) {

      const m = error.response?.data?.message || t.errGeneric;
      setReturnError(typeof m === 'string' ? m : t.errGeneric);
    } finally {
      setDecisionLoading(false);
    }
  };

  const downloadFile = async (fileUrl, fileName) => {
    try {
      const response = await axios.get(fileUrl, {
        headers: { ...authHeaders, Accept: 'application/octet-stream' },
        responseType: 'blob',
      });
      const fileType = response.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([response.data], { type: fileType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || 'file');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {

      alert(t.fileDownloadErr);
    }
  };

  const downloadFileWithProgress = async (idx, fileUrl, fileName) => {
  try {
    setDlProgress(p => ({ ...p, [idx]: null })); // запустили: показываем спиннер
    const res = await fetch(fileUrl, {
  credentials: "include",
  headers: {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    "Accept-Language": locale,
    Accept: "application/octet-stream",
  },
});

    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const total = Number(res.headers.get("Content-Length") || 0);
    const reader = res.body?.getReader?.();

    if (!reader) {
      // нет стримов (редко) — просто откроем URL
      window.location.href = fileUrl;
      return;
    }

    const chunks = [];
    let received = 0;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      received += value.length;
      if (total > 0) {
        const pct = Math.min(100, Math.round((received / total) * 100));
        setDlProgress(p => ({ ...p, [idx]: pct }));
      }
    }

    const blob = new Blob(chunks);
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = fileName || "file";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1500);
  } catch (e) {
    alert(t.fileDownloadErr);
    // fallback на старую функцию (axios blob), если хочешь:
    // await downloadFile(fileUrl, fileName);
  } finally {
    setDlProgress(p => {
      const { [idx]: _, ...rest } = p;
      return rest;
    });
  }
};


  if (loading) return <p>{t.loading}</p>;
  if (!application) return <p>{t.notFound}</p>;

  return (
    <div className="application-view-container">
      <div className="application-header">
        <h2 className="application-title">{application.title}</h2>
      </div>

      {/* Previous expertise toggle */}
      {hasPrevious && (
        <div className="previous-expertise-toggle">
          <button
            className={`prev-exp-btn prev-exp-btn--ghost ${prevOpen ? 'open' : ''}`}
            onClick={togglePrevious}
          >
            <span className="chev" aria-hidden="true"></span>
            <span className="label">{t.prevBtn}</span>
            <span className="hint">{prevOpen ? t.close : t.open}</span>
          </button>
        </div>
      )}

      {/* Previous expertise content */}
      {hasPrevious && prevOpen && (
        <section className="application-section previous-expertise">
          {prevLoading && <p>{t.loading}</p>}
          {prevError && <p className="text-error">{prevError}</p>}

          {!prevLoading && !prevError && previousData && (
            <>
              <h3 className="section-title">{t.prevTitle}</h3>
              <table className="info-table">
                <tbody>
                  <InfoRow label={t.name} value={previousData.title} />
                  <InfoRow label={t.publisher} value={previousData.publisher?.organization_name || previousData.publisher} />
                  <InfoRow label={t.subject} value={pickName(previousData.subject)} />
                  <InfoRow label={t.grade} value={pickName(previousData.grade)} />
                  <InfoRow label={t.devLang} value={pickName(previousData.development_language)} />
                  <InfoRow
                    label={t.decisionResult}
                    value={
                      previousData.decision?.status
                        ? (previousData.decision.status === 'accept' ? (locale === 'ru' ? 'Принято' : 'Қабылданды') : (locale === 'ru' ? 'Возвращено' : 'Өңдеуге қайтарылды'))
                        : (previousData.final_status_text || pickName(previousData.status))
                    }
                  />
                  <InfoRow label={t.avgScore} value={previousData.average_score ?? previousData.final_average ?? '-'} />
                  <InfoRow
                    label={t.decisionDate}
                    value={
                      previousData.decision?.date
                        ? fmtDate(previousData.decision.date)
                        : (previousData.finished_at ? fmtDate(previousData.finished_at) : '-')
                    }
                  />
                </tbody>
              </table>

              {(previousData.final_decision_file_path || previousData.final_document?.download_url) && (
                <div style={{ marginTop: 12 }}>
                  <button
                    className="download-btn"
                    onClick={() => {
                      const url = previousData.final_document?.download_url
                        ? (previousData.final_document.download_url.startsWith('http')
                            ? previousData.final_document.download_url
                            : `${apiUrl}${previousData.final_document.download_url}`)
                        : `${apiUrl}/api/applications/${previousData.id}/final-decision/download`;
                      downloadFile(url, `final_decision_${previousData.id}.docx`);
                    }}
                  >
                    {t.downloadFinal}
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      )}

      <section className="application-section">
        <h3 className="section-title">{t.mainTitle}</h3>
        <table className="info-table">
          <tbody>
            <InfoRow label={t.publisher} value={application.publisher?.organization_name} />
            <InfoRow label={t.authors} value={application.authors} />
            <InfoRow label={t.grade} value={pickName(application.grade)} />
            <InfoRow label={t.subject} value={pickName(application.subject)} />
            <InfoRow label={t.devLang} value={pickName(application.development_language)} />
            <InfoRow label={t.teachLang} value={pickName(application.teaching_language)} />
            <InfoRow label={t.category} value={pickName(application.category)} />
            <InfoRow label={t.serviceType} value={pickName(application.service_type)} />
            <InfoRow label={t.status} value={statusName} />
          </tbody>
        </table>
      </section>

      {application.archive_path && (
        <div style={{ marginTop: '20px' }}>
          <h4>{t.archive}</h4>
          <button
            className="download-btn"
            onClick={() => {
              const archiveUrl = `${apiUrl}/api/applications/${application.id}/download-archive`;
              const fileName = (locale === 'ru'
                ? `${userName.toLowerCase()}_архив.zip`
                : `${userName.toLowerCase()}_архив.zip`); // имя одинаковое — при желании локализуйте
              downloadFile(archiveUrl, application.archive_original_name || fileName);
            }}
          >
            {t.downloadArchive}
          </button>
        </div>
      )}

      <div className="decision-approvals-row">
        {application.decision && (
          <section className="status-box">
            <h3 className="section-title">{t.govDecision}</h3>
            <p>
              <strong>{t.status}:</strong>{' '}
              {application.decision.status === 'accept' ? (
                <span className="status-accept"> {t.approved}</span>
              ) : (
                <span className="status-return"> {t.returned}</span>
              )}
            </p>
            {application.decision.comment && (
              <p><strong>{t.comment}:</strong> {application.decision.comment}</p>
            )}
            <p><strong>{t.decidedBy}:</strong> {application.decision.by}</p>
            <p><strong>{t.date}:</strong> {fmtDate(application.decision.date)}</p>
          </section>
        )}

        {userRole === 'директор' && application.approvals?.length > 0 && (
          <section className="status-box">
            <h3 className="section-title">{t.signingStatus}</h3>
            {[...application.approvals]
              .reverse()
              .filter((item, index, self) => index === self.findIndex(a => a.role === item.role))
              .map((a, idx) => (
                <div key={idx} className="approval-item">
                  <span className="approval-role">{a.role}:</span>
                  <span className="approval-user">{a.user || '—'}</span>
                  {a.approved === 1 ? (
                    <>
                      <span className="approval-status approved">{t.approvedYes}</span>
                      {a.approved_at && (
                        <span className="approval-date">({fmtDate(a.approved_at)})</span>
                      )}
                    </>
                  ) : (
                    <span className="approval-status pending">{t.pending}</span>
                  )}
                </div>
              ))}
          </section>
        )}
      </div>

      {application.files?.length > 0 && (
        <section className="application-section files-section">
          <h3 className="section-title">{t.filesTitle}</h3>
          <div className="file-button-container">
            {application.files.map((file, index) => {
              const hasFile = !!file.download_url;
              const hasLink = !!file.link_url;
              if (!hasFile && !hasLink) return null;

              const fullDownloadUrl = hasFile
  ? (file.download_url.startsWith('http') ? file.download_url : joinUrl(apiUrl, file.download_url))
  : null;


              const fileName = locale === 'ru'
                ? `${userName.toLowerCase()}_${index + 1}_проверка.pdf`
                : `${userName.toLowerCase()}_${index + 1}_тексеріс.pdf`;

              return (
                <div key={index} className="file-button-group">
                  {fullDownloadUrl && (
  <button
    className={`download-chip ${dlProgress[index] !== undefined ? 'is-loading' : ''}`}
    onClick={() => downloadFileWithProgress(index, fullDownloadUrl, fileName)}
    disabled={dlProgress[index] !== undefined}
  >
    {dlProgress[index] === undefined && (
      <>
        {file.section || `${index + 1}${locale === 'ru' ? t.sectionNum : t.sectionNum}`}{" "}
        ({locale === 'ru' ? 'скачать' : 'жүктеу'})
      </>
    )}

    {dlProgress[index] !== undefined && (
      <span className="loader-wrap">
        <span className="loader-spinner" aria-hidden />
        <span className="loader-text">
          {typeof dlProgress[index] === 'number'
            ? `${dlProgress[index]}%`
            : (locale === 'ru' ? 'Загрузка…' : 'Жүктелуде…')}
        </span>
      </span>
    )}
  </button>
)}


                  {hasLink && (
                    <a
                      href={file.link_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="link-chip"
                    >
                      {t.fileLink}
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {application.logs?.length > 0 && (
        <section className="application-section logs-section">
          <button
            className={`log-toggle-button ${logsVisible ? 'open' : ''}`}
            onClick={() => setLogsVisible(!logsVisible)}
          >
            {t.logs} <span className="arrow-icon">▼</span>
          </button>

          <div className={`log-cards-wrapper ${logsVisible ? 'open' : 'closed'}`}>
            <div className="log-cards">
              {application.logs.map((log, idx) => (
                <div key={idx} className="log-card">
                  <div className="log-header">
                    <strong>{log.status}</strong> <span className="log-type">({log.type})</span>
                  </div>
                  <div className="log-body">
                    <span className="log-user">{log.user || '—'}</span>
                    <span className="log-date">{fmtDate(log.date)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {showDecisionUI && (
        <div className="decision-buttons decision-buttons--highlighted">
          <button
            className="decision-btn accept"
            onClick={() => handleDecision('accept')}
            disabled={decisionLoading}
          >
            {t.accept}
          </button>
          <button
            className="decision-btn reject"
            onClick={() => handleDecision('return')}
            disabled={decisionLoading}
          >
            {t.return}
          </button>
        </div>
      )}

      {/* Return modal */}
      {showReturnModal && (
        <div className="modal-overlay">
          <div className="modal-inner">
            <div className="modal-header">
              <h3>{t.returnTitle}</h3>
              <button
                onClick={() => { setShowReturnModal(false); setReturnComment(''); setReturnError(''); }}
                className="modal-close"
                aria-label="Close"
              >
                ✖
              </button>
            </div>

            <div className="modal-body">
              <label htmlFor="return-comment" style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>
                {t.returnLabel}
              </label>
              <textarea
                id="return-comment"
                value={returnComment}
                onChange={(e) => setReturnComment(e.target.value)}
                maxLength={2000}
                rows={5}
                placeholder={t.returnPlaceholder}
                style={{ width: '100%', padding: 10, borderRadius: 8 }}
                disabled={decisionLoading}
              />
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:8 }}>
                <small style={{ opacity:.7 }}>{returnComment.length}/2000</small>
                {returnError && <small style={{ color:'#b91c1c', fontWeight:600 }}>{returnError}</small>}
              </div>
            </div>

            <div className="modal-actions" style={{ marginTop: 12, display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button
                className="decision-btn reject"
                onClick={submitReturnDecision}
                disabled={decisionLoading}
              >
                {t.returnConfirm}
              </button>
              <button
                className="decision-btn"
                onClick={() => { setShowReturnModal(false); setReturnComment(''); setReturnError(''); }}
                disabled={decisionLoading}
              >
                {t.cancel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  function InfoRow({ label, value }) {
    return (
      <tr>
        <td className="label">{label}</td>
        <td className="value">{(value ?? '-') || '-'}</td>
      </tr>
    );
  }
}
